<template>
  <b-container class="pt-3">
    <b-card
      no-body
      hide-footer>
      <b-tabs
        v-model="tabIndex"
        card
        @input="onPlatformSelected">
        <b-tab
          v-for="platform in platforms"
          :key="platform.id"
          :title="platform.name">
          <b-card-group deck>
            <b-card
              no-body>
              <template slot="header">
                <div class="d-flex align-items-center">
                  <div class="flex-grow-1">
                    <span class="m-0">
                      Configs
                    </span>
                  </div>
                  <div>
                    <b-dropdown
                      text="Create"
                      size="sm"
                      variant="primary">
                      <b-dropdown-item v-b-modal.newMajorConfig>
                        New Major Version
                      </b-dropdown-item>
                      <b-dropdown-item v-b-modal.newMinorConfig>
                        New Minor Version
                      </b-dropdown-item>
                      <b-dropdown-item
                        v-b-modal.cloneMajorConfig
                        disabled>
                        Clone Major from Existing
                      </b-dropdown-item>
                      <b-dropdown-item
                        v-b-modal.cloneMinorConfig
                        disabled>
                        Clone Minor from Existing
                      </b-dropdown-item>
                    </b-dropdown>
                  </div>
                </div>
              </template>
              <b-list-group flush>
                <b-list-group-item
                  v-for="(config, index) in configs"
                  :key="config.version"
                  :btn="true">
                  <span
                    v-b-toggle="`collapse-${config.version}`"
                    aria-expanded="false"
                    class="pr-2">
                    <i class="fas fa-caret-down" />
                  </span>
                  <span
                    class="clickable font-weight-bold"
                    @click.prevent="$router.push({ path: '/', query: { platformId: platform.id.toString(), majorId: config.id.toString() } })">
                    {{ config.version }}.x.x
                  </span>
                  <b-btn
                    variant="outline-primary"
                    :pressed="config.id === selectedMajor.id && !selectedMinor.id"
                    size="sm"
                    class="float-right edit-btn"
                    @click.prevent="$router.push({ path: '/', query: { platformId: platform.id.toString(), majorId: config.id.toString() } })">
                    Edit
                  </b-btn>

                  <div class="pt-2">
                    <b-collapse
                      :id="`collapse-${config.version}`"
                      :visible="index === selectedMajorIndex">
                      <b-list-group class="pt-2">
                        <b-list-group-item
                          v-for="minor in config.minors"
                          :key="minor.id"
                          class="list-group-item-minor">
                          <span
                            class="clickable"
                            @click.prevent="$router.push({ path: '/', query: { platformId: platform.id.toString(), majorId: config.id.toString(), minorId: minor.id.toString() } })">
                            {{ minor.version }}
                          </span>
                          <b-btn
                            variant="outline-primary"
                            :pressed="config.id === selectedMajor.id && minor.id === selectedMinor.id"
                            size="sm"
                            class="float-right edit-btn"
                            @click.prevent="$router.push({ path: '/', query: { platformId: platform.id.toString(), majorId: config.id.toString(), minorId: minor.id.toString() } })">
                            Edit
                          </b-btn>
                        </b-list-group-item>
                      </b-list-group>
                    </b-collapse>
                  </div>
                </b-list-group-item>
              </b-list-group>
            </b-card>
            <b-card no-body>
              <template slot="header">
                <div class="d-flex align-items-center">
                  <div class="flex-grow-1">
                    <span class="m-0">
                      Files
                    </span>
                  </div>
                  <div>
                    <b-btn
                      v-b-modal.newFile
                      :disabled="!selectedMajor.id"
                      variant="primary"
                      size="sm">
                      New File
                    </b-btn>
                  </div>
                </div>
              </template>
              <b-list-group flush>
                <b-list-group-item
                  v-if="!files || !selectedMajor.id"
                  class="text-muted font-italic">
                  Please select a config
                </b-list-group-item>
                <b-list-group-item
                  v-else-if="files && !files.length"
                  class="text-muted font-italic">
                  No files exist for this config
                </b-list-group-item>
                <b-list-group-item
                  v-for="file in files"
                  v-else
                  :key="file.id"
                  href="#"
                  @click.prevent="onFileSelected(file)">
                  <span>{{ file.name }}</span>
                  <b-badge
                    v-if="file.overrideFileId"
                    class="ml-2"
                    pill
                    variant="info">
                    override exists
                  </b-badge>
                </b-list-group-item>
              </b-list-group>
            </b-card>
          </b-card-group>
        </b-tab>
      </b-tabs>
    </b-card>
    <new-major-config-modal
      id="newMajorConfig"
      :platform-id="selectedPlatform ? selectedPlatform.id : null"
      :configs="configs" />
    <new-major-config-modal
      id="cloneMajorConfig"
      :platform-id="selectedPlatform ? selectedPlatform.id : null"
      :configs="configs"
      clone />
    <new-minor-config-modal
      id="newMinorConfig"
      :platform-id="selectedPlatform ? selectedPlatform.id : null"
      :configs="configs" />
    <new-minor-config-modal
      id="cloneMinorConfig"
      :platform-id="selectedPlatform ? selectedPlatform.id : null"
      :configs="configs"
      clone />
    <new-file-modal
      id="newFile"
      :platform-id="selectedPlatform ? selectedPlatform.id : null"
      :major-id="selectedMajor ? selectedMajor.id : null"
      :configs="configs"
      @refresh-files="onPlatformSelected(tabIndex)" />
  </b-container>
</template>

<script>
  import { Config, Override } from '@/services/api'
  import { mapActions, mapGetters, mapMutations } from 'vuex'
  import {SELECTED_FILE} from "@/store/keys"
  import NewMajorConfigModal from "@/components/NewMajorConfigModal"
  import NewMinorConfigModal from "@/components/NewMinorConfigModal"
  import NewFileModal from "@/components/NewFileModal"

  export default {
    name: "Configs",
    components: {NewFileModal, NewMinorConfigModal, NewMajorConfigModal},
    data() {
      return {
        configs: [],
        files: [],
        tabIndex: null,
        selectedMajorIndex: 0,
        selectedPlatform: null,
        selectedMajor: {},
        selectedMinor: {},
      }
    },
    computed: {
      ...mapGetters([
        'platforms',
      ]),
    },
    async mounted() {
      const params = {
        platformID: this.$route.query.platformId,
        majorID: this.$route.query.majorId,
        minorID: this.$route.query.minorId,
      }

      // load the platforms into the Vuex store.
      // this is relatively static data so only load it once.
      if (!this.platforms.length) {
        await this.retrievePlatforms()
      }

      this.refreshData(params)
    },
    methods: {
      ...mapMutations({
        storeStateFile: SELECTED_FILE,
      }),
      ...mapActions([
        'retrievePlatforms',
      ]),
      async refreshData({platformID, majorID, minorID}) {
        // navigate to appropriate platform/version if coming from a file edit breadcrumb
        if (platformID) {
          this.$nextTick(() => {
            this.tabIndex = this.platforms.findIndex(p => p.id === parseInt(platformID))
            if (majorID) {
              this.$nextTick(async () => {
                await this.onPlatformSelected(this.tabIndex)
                this.selectedMajorIndex = this.configs.findIndex(c => c.id === parseInt(majorID))
                const config = this.configs.find(c => c.id === parseInt(majorID))
                if (config) {
                  if (minorID) {
                    const minor = config.minors.find(m => m.id === parseInt(minorID))
                    if (minor) {
                      this.onConfigSelected(config, minor)
                    }
                  } else {
                    this.onConfigSelected(config)
                  }
                }
              })
            }
          })
        }
      },
      async onPlatformSelected(index) {
        const selectedPlatform = this.platforms[index]
        if (selectedPlatform) {
          this.selectedPlatform = selectedPlatform
          this.configs = await Config.configs(this.selectedPlatform.id)
        }
      },
      async onConfigSelected(config, minor) {
        this.selectedMajor = config
        this.selectedMinor = minor || {}
        this.files = await Config.files(this.selectedPlatform.id, this.selectedMajor.id, this.selectedMinor.id)
      },
      async onFileSelected(file) {
        this.storeStateFile(file)
        if (this.selectedMinor.id && file.overrideFileId) {
          this.$router.push({path: `/overridefiles/edit/${file.overrideFileId}`})
        } else if (this.selectedMinor.id) {
          const modalConfirmed = await this.$confirm.open(`Create override to ${file.name} for version ${this.selectedMinor.version}?`)
          if (modalConfirmed) {
            const overrideFileResult = await Override.create(this.selectedMinor.id, file.id)
            this.$router.push({path: `/overridefiles/edit/${overrideFileResult.id}`})
          }
        } else {
          this.$router.push({path: `/files/edit/${file.id}`})
        }
      },
    },
    beforeRouteUpdate(to, from, next) {
      const params = {
        platformID: to.query.platformId,
        majorID: to.query.majorId,
        minorID: to.query.minorId,
      }
      this.refreshData(params)
      next()
    },
  }
</script>

<style scoped>
  @import "~@fortawesome/fontawesome-free/css/all.min.css";

  .list-group-item-minor {
    padding-top: .5rem;
    padding-bottom: .5rem;
    padding-right: 0;
    background-color: #f8f9fa;
    border-radius: 0.25rem;
  }

  .list-group-item .fas {
    transition: .3s transform ease-in-out;
  }

  .list-group-item .fas:hover {
    cursor: pointer;
  }

  .list-group-item .collapsed .fas {
    transform: rotate(-90deg);
  }

  a.small {
    padding-top: 3px;
  }

  .edit-btn {
    padding-top: 0.1rem;
    padding-bottom: 0.1rem;
  }

</style>